import { useCallback, useEffect, useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { IANA_TIME_ZONES } from "@src/features/commsOptIn/launchPick";
import {
  CompleteForm_TimeslotFragment,
  useCompleteForm_FindTimeslotDraftMutation,
  useCompleteForm_OrgTimeZoneQuery,
  useCompleteForm_TimeslotsQuery,
} from "./useLocationTimeslots.generated";
import { draftCandidateSlots, MAX_TIMEOUT_MS, pickTimeslot } from "./timeslots";

// Past-due slots have already ended, so look back far enough to keep any
// late window in the list (mobile looks back the same week).
const LOOKBACK_DAYS = 7;
const MAX_TIMESLOTS = 500;
const REFRESH_MS = 60_000;

// Times show in the store's zone, else the org's.
export const useTimeslotZone = (
  locationZone: string | null | undefined,
  { skip = false }: { skip?: boolean } = {},
): string => {
  const { data } = useCompleteForm_OrgTimeZoneQuery({
    skip: skip || !!locationZone,
  });
  const orgZone = data?.Me?.user.org?.timezone;
  return (
    locationZone ||
    (orgZone ? IANA_TIME_ZONES[orgZone] : undefined) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
};

export type LocationTimeslots = {
  loading: boolean;
  error: boolean;
  slot: CompleteForm_TimeslotFragment | undefined;
  started: boolean;
  slots: CompleteForm_TimeslotFragment[];
  candidates: CompleteForm_TimeslotFragment[];
  startedAtBySlotId: Record<string, string | null | undefined>;
  zone: string;
  now: number;
  retry: () => void;
};

export const useLocationTimeslots = ({
  libraryItemId,
  skillId,
  locationId,
  skip,
}: {
  libraryItemId: string;
  skillId: number | undefined;
  locationId: number;
  skip: boolean;
}): LocationTimeslots => {
  const [openedAt] = useState(() => Date.now());
  const { data, loading, error, refetch } = useCompleteForm_TimeslotsQuery({
    variables: {
      input: {
        filters: {
          libraryItemIds: { value: [libraryItemId] },
          locationIds: { value: [locationId] },
          endAfter: {
            value: new Date(
              openedAt - LOOKBACK_DAYS * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        },
      },
      pagination: { limit: MAX_TIMESLOTS },
    },
    skip,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: false,
  });
  const slots = useMemo(
    () => data?.CompletionScheduleInstances.objects ?? [],
    [data],
  );

  // Statuses come from the server; refetch at the next boundary (and every
  // minute) so an opening or closing slot moves the pick.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (skip) return;
    const boundaries = slots
      .flatMap((s) => [s.start, s.end, s.lateDeadline])
      .map((iso) => (iso ? new Date(iso).getTime() : NaN))
      .filter((t) => t > now);
    const next = Math.min(now + REFRESH_MS, ...boundaries);
    const timeout = setTimeout(
      () => {
        setNow(Date.now());
        refetch().catch(() => undefined);
      },
      Math.min(next - now + 1000, MAX_TIMEOUT_MS),
    );
    return () => clearTimeout(timeout);
  }, [now, refetch, skip, slots]);

  const candidates = useMemo(
    () => draftCandidateSlots(slots, now),
    [slots, now],
  );
  const candidateKey = candidates.map((s) => s.id).join(",");
  const draftKey = `${libraryItemId}:${locationId}:${skillId}:${candidateKey}`;
  const [retryCount, setRetryCount] = useState(0);
  const [draftError, setDraftError] = useState<string>();
  const [findDraft] = useCompleteForm_FindTimeslotDraftMutation();
  const [drafts, setDrafts] = useState<{
    key: string;
    startedAtBySlotId: Record<string, string | null | undefined>;
  }>();
  useEffect(() => {
    if (skip || !skillId || !data) return;
    const ids = candidateKey ? candidateKey.split(",") : [];
    let cancelled = false;
    setDraftError(undefined);
    Promise.all(
      ids.map((completionScheduleInstanceId) =>
        findDraft({
          variables: {
            input: {
              skillId,
              locationId,
              completionScheduleInstanceId,
              createIfMissing: false,
            },
          },
        }).then(
          (result) =>
            result.data?.getOrCreateTaskListInstanceDraft.skillInstance
              ?.startedAt,
        ),
      ),
    )
      .then((found) => {
        if (cancelled) return;
        setDrafts({
          key: draftKey,
          startedAtBySlotId: Object.fromEntries(
            ids.map((id, index) => [id, found[index]]),
          ),
        });
      })
      .catch(() => {
        if (!cancelled) setDraftError(draftKey);
      });
    return () => {
      cancelled = true;
    };
  }, [
    candidateKey,
    data,
    draftKey,
    findDraft,
    locationId,
    now,
    retryCount,
    skillId,
    skip,
  ]);

  const draftsReady = drafts?.key === draftKey;
  const startedAtBySlotId = useMemo(
    () => (draftsReady ? drafts?.startedAtBySlotId ?? {} : {}),
    [drafts, draftsReady],
  );
  const slot = useMemo(
    () => pickTimeslot(slots, startedAtBySlotId, now),
    [now, slots, startedAtBySlotId],
  );
  const zone = useTimeslotZone(
    slot?.location.address?.timezoneStr ??
      slots[0]?.location.address?.timezoneStr,
    { skip },
  );
  const retry = useCallback(() => {
    setRetryCount((value) => value + 1);
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    loading: !skip && ((loading && !data) || (!!data && !draftsReady)),
    error: (!!error && !data) || draftError === draftKey,
    slots,
    candidates,
    startedAtBySlotId,
    slot,
    started: !!slot && !!startedAtBySlotId[slot.id],
    zone,
    now,
    retry,
  };
};

gql`
  query CompleteForm_Timeslots(
    $input: CompletionScheduleInstancesInput!
    $pagination: PaginationInput
  ) {
    CompletionScheduleInstances(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...CompleteForm_Timeslot
      }
    }
  }

  query CompleteForm_OrgTimeZone {
    Me {
      user {
        id
        org {
          id
          timezone
        }
      }
    }
  }

  mutation CompleteForm_FindTimeslotDraft(
    $input: GetOrCreateTaskListInstanceDraftInput!
  ) {
    getOrCreateTaskListInstanceDraft(input: $input) {
      skillInstance {
        id
        startedAt
      }
    }
  }

  fragment CompleteForm_Timeslot on CompletionScheduleInstance {
    id
    start
    end
    lateDeadline
    status
    completedBySkillInstance {
      id
    }
    location {
      id
      address {
        id
        timezoneStr
      }
    }
  }
`;
